import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CalendarIcon, MessageCircle, Loader2 } from "lucide-react";
import { T } from "@/hooks/useAITranslate";

const WA_PHONE = "212706280215";
const FORMULAS = [
  { key: "private", value: "Privé (1 élève)", label: "Privé — 1 élève" },
  { key: "semi", value: "Semi-privé (2 élèves)", label: "Semi-privé — 2 élèves" },
  { key: "group", value: "Groupe (4 à 6 élèves)", label: "Groupe — 4 à 6 élèves" },
] as const;
const DURATIONS = ["1h", "2h", "4h", "6h", "8h", "10h"] as const;
type FormulaKey = (typeof FORMULAS)[number]["key"];
type DurationKey = (typeof DURATIONS)[number];

type CourseRow = {
  id: string;
  title: string;
  category: string;
  currency: string;
  price: number;
  price_private_1h: number; price_private_2h: number; price_private_4h: number; price_private_6h: number; price_private_8h: number; price_private_10h: number;
  price_semi_private_1h: number; price_semi_private_2h: number; price_semi_private_4h: number; price_semi_private_6h: number; price_semi_private_8h: number; price_semi_private_10h: number;
  price_group_1h: number; price_group_2h: number; price_group_4h: number; price_group_6h: number; price_group_8h: number; price_group_10h: number;
};

const priceFor = (c: CourseRow | undefined, f: FormulaKey | "", d: DurationKey | ""): number => {
  if (!c || !f || !d) return 0;
  const suffix = d;
  const key =
    f === "private" ? `price_private_${suffix}` :
    f === "semi"    ? `price_semi_private_${suffix}` :
                      `price_group_${suffix}`;
  return Number((c as any)[key] || 0);
};

const Schema = z.object({
  name: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(40).optional(),
  activity: z.string().trim().max(120).optional(),
  formula: z.string().trim().max(60).optional(),
  target_date: z.date().optional(),
  needs_transport: z.boolean(),
  message: z.string().trim().max(2000).optional(),
});

const SurfBookingForm = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [courseId, setCourseId] = useState<string>("");
  const [formulaKey, setFormulaKey] = useState<FormulaKey | "">("");
  const [duration, setDuration] = useState<DurationKey | "">("");
  const [courseHint, setCourseHint] = useState<string>("");
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [transport, setTransport] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load courses for live pricing.
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("surf_courses")
        .select(
          "id, title, category, currency, price, price_private_1h, price_private_2h, price_private_4h, price_private_6h, price_private_8h, price_private_10h, price_semi_private_1h, price_semi_private_2h, price_semi_private_4h, price_semi_private_6h, price_semi_private_8h, price_semi_private_10h, price_group_1h, price_group_2h, price_group_4h, price_group_6h, price_group_8h, price_group_10h"
        )
        .eq("is_published", true)
        .order("slot", { ascending: true });
      setCourses((data as CourseRow[]) || []);
    };
    load();
    const channel = supabase
      .channel("surf_booking_courses")
      .on("postgres_changes", { event: "*", schema: "public", table: "surf_courses" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Sync selection coming from a course card click.
  useEffect(() => {
    const apply = () => {
      try {
        const raw = sessionStorage.getItem("surf_selected_formula");
        if (!raw) return;
        const parsed = JSON.parse(raw) as { formula?: string; course?: string; duration?: string };
        if (parsed?.formula) {
          const m = FORMULAS.find((f) => f.value.startsWith(parsed.formula!) || f.label.startsWith(parsed.formula!));
          if (m) setFormulaKey(m.key);
        }
        if (parsed?.duration && (DURATIONS as readonly string[]).includes(parsed.duration)) {
          setDuration(parsed.duration as DurationKey);
        }
        if (parsed?.course) setCourseHint(parsed.course);
      } catch {}
    };
    apply();
    window.addEventListener("surf:formula-selected", apply);
    return () => window.removeEventListener("surf:formula-selected", apply);
  }, []);

  // Resolve courseId from courseHint when courses load.
  useEffect(() => {
    if (!courseHint || courseId) return;
    const found = courses.find((c) => c.title === courseHint);
    if (found) setCourseId(found.id);
  }, [courseHint, courses, courseId]);

  const selectedCourse = courses.find((c) => c.id === courseId);
  const livePrice = priceFor(selectedCourse, formulaKey, duration);
  const availableDurations = (DURATIONS as readonly DurationKey[]).filter((d) =>
    selectedCourse && formulaKey ? priceFor(selectedCourse, formulaKey, d) > 0 : false
  );
  // Reset duration if it's no longer available for the current course/formula
  useEffect(() => {
    if (duration && !availableDurations.includes(duration)) {
      setDuration("");
    }
  }, [courseId, formulaKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const currency = selectedCourse?.currency || "EUR";
  const priceLabel =
    !selectedCourse || !formulaKey || !duration
      ? "Sélectionnez un cours, une formule et une durée"
      : livePrice > 0
        ? `${livePrice} ${currency}`
        : "Prix sur demande";

  const formulaLabel = FORMULAS.find((f) => f.key === formulaKey)?.value || "";

  const buildWaMessage = () => {
    const priceLine =
      selectedCourse && formulaKey && duration
        ? (livePrice > 0 ? `${livePrice} ${currency}` : "Prix sur demande")
        : "—";
    const lines: string[] = [
      "Bonjour Maroc Surfing, je souhaite réserver une session :",
      `- Nom : ${name.trim() || "—"}`,
      `- Téléphone : ${phone.trim() || "—"}`,
      `- Cours : ${selectedCourse?.title || courseHint || "—"}`,
      `- Formule : ${formulaLabel || "—"}`,
      `- Durée : ${duration || "—"}`,
      `- Prix : ${priceLine}`,
      `- Date souhaitée : ${date ? format(date, "PPP", { locale: fr }) : "—"}`,
      `- Transport (aéroport / hôtel) : ${transport ? "Oui" : "Non"}`,
    ];
    if (message.trim()) {
      lines.push(`- Message : ${message.trim()}`);
    }
    lines.push("", "Source : page Maroc Surfing");
    return lines.join("\n");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Schema.safeParse({
      name: name || undefined,
      phone: phone || undefined,
      activity: selectedCourse?.title || undefined,
      formula: formulaLabel || undefined,
      target_date: date,
      needs_transport: transport,
      message: message || undefined,
    });
    if (!parsed.success) {
      toast.error("Certains champs dépassent la longueur autorisée.");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("surf_leads").insert({
      name: name.trim(),
      phone: phone.trim(),
      activity: selectedCourse?.title || "",
      formula: formulaLabel || "",
      target_date: date ? format(date, "yyyy-MM-dd") : null,
      needs_transport: transport,
      message: message.trim() || null,
      status: "new",
      source: "maroc-surfing",
    });
    setSubmitting(false);

    if (error) {
      console.error("[SurfBookingForm] insert failed:", error);
      toast.error("Envoi enregistré sur WhatsApp uniquement.");
    } else {
      toast.success("Demande enregistrée, ouverture de WhatsApp…");
    }

    const url = `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(buildWaMessage())}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-6 space-y-4"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            <T>Nom complet</T>
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 120))}
            placeholder="Ex : Camille Dupont"
            maxLength={120}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            <T>Numéro de téléphone</T>
          </label>
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.slice(0, 40))}
            placeholder="+33 6 12 34 56 78"
            inputMode="tel"
            maxLength={40}
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            <T>Service souhaité</T>
          </label>
          <Select value={courseId} onValueChange={(v) => setCourseId(v)}>
            <SelectTrigger>
              <SelectValue placeholder={courses.length ? "Choisir un cours" : "Chargement…"} />
            </SelectTrigger>
            <SelectContent>
              {courses.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            <T>Formule (effectif)</T>
          </label>
          <Select value={formulaKey} onValueChange={(v) => setFormulaKey(v as FormulaKey)}>
            <SelectTrigger>
              <SelectValue placeholder="Privé / Semi-privé / Groupe" />
            </SelectTrigger>
            <SelectContent>
              {FORMULAS.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            <T>Durée</T>
          </label>
          <Select value={duration} onValueChange={(v) => setDuration(v as DurationKey)}>
            <SelectTrigger>
              <SelectValue placeholder={
                !selectedCourse || !formulaKey
                  ? "Choisir cours et formule d'abord"
                  : availableDurations.length === 0
                    ? "Aucune durée disponible"
                    : "Choisir une durée"
              } />
            </SelectTrigger>
            <SelectContent>
              {availableDurations.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            <T>Date exacte</T>
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP", { locale: fr }) : "Choisir une date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                initialFocus
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Live price */}
      <div className="rounded-lg border border-primary/40 bg-accent/10 px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-xs text-primary">
          <p className="font-semibold"><T>Prix estimé</T></p>
          <p className="text-[11px] text-gray-600">
            <T>Calculé en temps réel d'après votre sélection.</T>
          </p>
        </div>
        <p
          key={`${courseId}-${formulaKey}-${duration}-${livePrice}`}
          className="text-lg sm:text-xl font-extrabold text-primary transition-all duration-200"
        >
          <T>{priceLabel}</T>
        </p>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-[#f7f9fb] px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-primary"><T>Besoin de transport ?</T></p>
          <p className="text-xs text-gray-600">
            <T>Transfert aéroport ou hôtel ↔ spot de surf via Maroc Transfers.</T>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("text-xs font-semibold", !transport && "text-primary")}><T>Non</T></span>
          <Switch checked={transport} onCheckedChange={setTransport} />
          <span className={cn("text-xs font-semibold", transport && "text-primary")}><T>Oui</T></span>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          <T>Message</T>
        </label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
          placeholder="Niveau, nombre de personnes, hébergement…"
          rows={4}
          maxLength={2000}
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-base py-6 rounded-xl"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
        ) : (
          <MessageCircle className="w-5 h-5 mr-2" />
        )}
        <T>Envoyer ma demande</T>
      </Button>
      <p className="text-[11px] text-gray-500 text-center">
        <T>Tous les champs sont facultatifs. Votre demande arrive sur WhatsApp et est enregistrée dans notre suivi commercial.</T>
      </p>
    </form>
  );
};

export default SurfBookingForm;